import React, { useEffect, useState } from "react";
import { getMentorshipCategories } from "./data/mentorshipData";

// Retries reading from localStorage instead of giving up after one try —
// how many times and how far apart.
const CATEGORY_RETRY_INTERVAL_MS = 500;
const CATEGORY_MAX_RETRIES = 10;

function CategoryButton({ category, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(category.id)}
      className="btn"
      style={{
        padding: "4px 18px",
        fontSize: "14px",
        whiteSpace: "nowrap",
        fontWeight: "400",
        borderRadius: "20px",
        background: active ? "#131722" : "#ffffff",
        color: active ? "#ffffff" : "#3a3a3a",
        border: active ? "none" : "1px solid #D7E0EA",
      }}
    >
      {category.name}
    </button>
  );
}

// Greedily drops each category into whichever row currently has less total
// text (a stand-in for pixel width), instead of a plain half-split — so
// both rows end up roughly the same length and both reach a "full" edge
// together, no matter how the label lengths happen to vary.
function splitIntoBalancedRows(categories) {
  const rows = [[], []];
  const rowWeights = [0, 0];
  categories.forEach((category) => {
    const lighterRow = rowWeights[0] <= rowWeights[1] ? 0 : 1;
    rows[lighterRow].push(category);
    rowWeights[lighterRow] += category.name.length;
  });
  return rows;
}

// Same pattern used for the Courses category chips: split into two rows and
// let the whole block scroll horizontally together, instead of wrapping to
// as many lines as needed.
function MentorshipFilters({ activeCategoryId, onSelect }) {
  const [categories, setCategories] = useState(getMentorshipCategories);

  // SplashData.home.subcategories (see data/mentorshipData.js) is written by
  // NavBar's own splash fetch, which can still be in flight the first time
  // this component reads localStorage — that race is why the filters
  // sometimes came back as just "All" and stayed that way forever, since a
  // one-shot read never re-checks. This retries a few times until the real
  // categories show up, instead of giving up after the first empty read.
  useEffect(() => {
    if (categories.length > 1) return undefined;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const fresh = getMentorshipCategories();
      if (fresh.length > 1) {
        setCategories(fresh);
        clearInterval(interval);
      } else if (attempts >= CATEGORY_MAX_RETRIES) {
        clearInterval(interval);
      }
    }, CATEGORY_RETRY_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [categories.length]);

  const [firstRow, secondRow] = splitIntoBalancedRows(categories);

  return (
    <div className="w-100 overflow-x-scroll scroll-hide my-3">
      <div className="d-flex gap-2 mb-2">
        {firstRow.map((category) => (
          <CategoryButton
            key={category.id}
            category={category}
            active={category.id === activeCategoryId}
            onSelect={onSelect}
          />
        ))}
      </div>
      <div className="d-flex gap-2">
        {secondRow.map((category) => (
          <CategoryButton
            key={category.id}
            category={category}
            active={category.id === activeCategoryId}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

export default React.memo(MentorshipFilters);
